/**
 * sharepoint.js
 *
 * Frontend API abstraction for SharePoint operations.
 * All calls go through the backend proxy at /api/sharepoint/*,
 * with the MSAL access token passed as a Bearer header.
 */

import { BACKEND_URL } from './config.js';
import { acquireGraphToken, maybeAcquireGraphToken } from './msalConfig';
import apiFetch from './apiFetch';

// -- pidly Documents behind the same calls ---------------------------------
// A document from the project's own library is opened in the viewer as a
// SharePoint-shaped record whose drive is `library:<project id>` and whose
// item is the library file's id. Everything that reads a SharePoint lock,
// makes a document read-only or offers Check out / Check in then works for
// it unchanged; only these calls look at the drive and go to the library.
const LIBRARY_DRIVE = 'library:';
export const libraryOrgOf = (driveId) =>
  (typeof driveId === 'string' && driveId.startsWith(LIBRARY_DRIVE) ? driveId.slice(LIBRARY_DRIVE.length) : null);
export const libraryDriveId = (orgId) => `${LIBRARY_DRIVE}${orgId}`;

async function libraryCall(orgId, tail, body) {
  const r = await apiFetch(`/api/orgs/${encodeURIComponent(orgId)}/library${tail}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    quiet: true,
  });
  let json = {};
  try { json = await r.json(); } catch { /* no body */ }
  if (!r.ok) {
    // Worded like SharePoint's refusal so the check-out handler names the holder.
    const err = new Error(r.status === 409 && json.lockedBy ? `Already checked out by ${json.lockedBy}` : (json.error || `Library error: ${r.status}`));
    err.status = r.status; err.body = json;
    throw err;
  }
  return json;
}

// Extract the pidly project ID from the current URL so the backend can
// decide whether to use an app-only (Sites.Selected) token or the user's
// delegated token for this SharePoint call. Routes like /project/:id/*
// are where SharePoint data flows originate.
function currentProjectId() {
  try {
    const m = window.location.pathname.match(/\/project\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

async function spFetch(endpoint, options = {}, { silent = false } = {}) {
  // Use maybeAcquireGraphToken so that pidly-only users (no Microsoft
  // account) aren't redirected to sign in. The backend falls back to
  // its app-only token for any drive bound to this project.
  const token = await maybeAcquireGraphToken();
  // Pidly identity travels in a separate header so the backend can tell
  // which pidly user is making the call — Microsoft sign-in may be shared
  // between two browsers but the pidly account is not.
  const pidlyToken = (() => {
    try { return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null; } catch { return null; }
  })();
  const projectId = currentProjectId();
  const res = await fetch(`${BACKEND_URL}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(pidlyToken ? { 'X-Pidly-Token': pidlyToken } : {}),
      ...(projectId ? { 'X-Pidly-Project-Id': projectId } : {}),
      'Content-Type': 'application/json',
    },
  });
  const json = await res.json();
  if (!res.ok) {
    // The server's whole answer rides on the error (diag, hint, code), so
    // a console line or a toast can say who asked and what to do.
    const err = new Error(json.error?.message || json.error || `SharePoint API error: ${res.status}`);
    err.status = res.status; err.body = json; err.hint = json.hint || null; err.diag = json.diag || null;
    throw err;
  }
  return json;
}

/** List SharePoint sites the user has access to. */
export const listSites = () => spFetch('/api/sharepoint/sites');

/** Any SharePoint route, with the pidly session and the workspace id on it. */
export const spRequest = (path, options = {}) => spFetch(path, options);

/**
 * Re-download ONE file into the backend's local file cache. Railway's disk
 * is wiped on every deploy, so any feature that reaches back to a source
 * PDF (stamp sharpen, vector-art harvest) hits a 404 after a deploy and
 * recovers through this. Must go through spFetch: the SharePoint routes
 * resolve the user from X-Pidly-Token, not the plain pidly Bearer — a
 * plain apiFetch call 401s, which is exactly how the recovery path sat
 * broken while looking implemented.
 */
export const ensureFileCached = (driveId, itemId, filename) =>
  libraryOrgOf(driveId) ? libraryCall(libraryOrgOf(driveId), `/files/${encodeURIComponent(itemId)}/cache`, {}) :
  spFetch('/api/sharepoint/ensure-cached', {
    method: 'POST',
    body: JSON.stringify({ driveId, itemId, filename }),
  });

/**
 * List the SharePoint Sites.Selected app-only bindings that apply to a
 * workspace. Bindings are scoped to the workspace's ORG (so every
 * workspace under the org inherits the connection); personal (org-less)
 * workspaces resolve to their own bindings. The backend resolves the
 * org/workspace scope from this projectId — the caller just passes the
 * workspace id. Used by the workspace loader to decide whether an MSAL
 * sign-in is required: if any binding applies, pidly's backend serves
 * files with its own app-only token and no Microsoft account is needed.
 */
export const fetchProjectSpSites = (projectId) =>
  spFetch(`/api/sharepoint/projects/${encodeURIComponent(projectId)}/sites`);

/**
 * List an org's SharePoint bindings directly (org-level scope). Used by
 * the project-settings page to show the connection status. Goes through
 * spFetch so the pidly identity travels in X-Pidly-Token (the SharePoint
 * routes resolve req.user from that header, not the plain pidly Bearer).
 */
export const fetchOrgSpSites = (orgId) =>
  spFetch(`/api/sharepoint/orgs/${encodeURIComponent(orgId)}/sites`);

/** List document libraries for a site. */
export const listDrives = (siteId) => spFetch(`/api/sharepoint/drives/${encodeURIComponent(siteId)}`);

/** List items in a drive root or folder. */
export const listItems = (driveId, itemId = null) =>
  itemId
    ? spFetch(`/api/sharepoint/items/${encodeURIComponent(driveId)}/${encodeURIComponent(itemId)}`)
    : spFetch(`/api/sharepoint/items/${encodeURIComponent(driveId)}`);

/** Download a file to local cache. Returns { cacheDir, filename, localPath }.
 * Pass `{ silent: true }` to throw instead of redirecting to MS sign-in
 * when there's no MSAL session — used by background recovery paths.
 * Pass `{ force: true }` to bypass the eTag cache-hit check and
 * overwrite the locally-cached copy with a fresh download (used by
 * the Re-sync from SharePoint action).
 */
export const downloadFile = (driveId, itemId, filename, opts = {}) =>
  libraryOrgOf(driveId) ? libraryCall(libraryOrgOf(driveId), `/files/${encodeURIComponent(itemId)}/cache`, { force: !!opts.force })
    // cacheHit here means "the bytes the viewer shows are the bytes this
    // copy holds". A checkout pulls the working copy from the SAME version
    // as the read copy on screen (`sameContent`), so the viewer only has to
    // follow the FOLDER (where saves land), not reload the page; a version
    // bump, a stale working copy or a forced pull still reloads.
    .then(r => ({ ...r, cacheHit: r.cacheHit || !!r.sameContent })) :
  spFetch('/api/sharepoint/download', {
    method: 'POST',
    body: JSON.stringify({ driveId, itemId, filename, force: !!opts.force }),
  }, opts);

/**
 * Fetch a SharePoint file as a streaming Response. The backend pipes Graph
 * CDN bytes to both disk and the response in parallel, so we get bytes as
 * they arrive AND the cache is populated — one transfer, two effects.
 *
 * Returns: { response: Response, filename: string, cacheDir: string, cacheHit: boolean }
 * The response body is the raw PDF bytes — use `response.blob()` to read.
 *
 * WARNING: only one caller can consume the response body. For shared access,
 * use fetchFileBlob below which dedups concurrent callers and returns a
 * reusable Blob.
 */
export async function fetchFileContent(driveId, itemId, filename, { silent = false } = {}) {
  // Prefer a Graph token if one is available, but don't redirect to
  // Microsoft if it isn't — the backend will fall back to its app-only
  // token (Sites.Selected) when the project has a binding for this drive.
  const token = await maybeAcquireGraphToken();
  const pidlyToken = (() => {
    try { return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null; } catch { return null; }
  })();
  const projectId = currentProjectId();
  const response = await fetch(`${BACKEND_URL}/api/sharepoint/content`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(pidlyToken ? { 'X-Pidly-Token': pidlyToken } : {}),
      ...(projectId ? { 'X-Pidly-Project-Id': projectId } : {}),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ driveId, itemId, filename }),
  });
  if (!response.ok) {
    // Error case: body is JSON { error: ... }, not PDF bytes
    let errMsg = `SharePoint content fetch failed: ${response.status}`;
    try {
      const body = await response.json();
      errMsg = body.error?.message || body.error || errMsg;
    } catch { /* body not JSON */ }
    throw new Error(errMsg);
  }
  return {
    response,
    filename: response.headers.get('X-Pidly-Filename') || filename,
    cacheDir: response.headers.get('X-Pidly-Cache-Dir') || null,
    cacheHit: response.headers.get('X-Pidly-Cache-Hit') === 'true',
  };
}

// ── Dedup + rate-limit for SP file fetches ─────────────────────────────
// React effects often fire the same file-open request multiple times as
// state updates cascade (folder sync, view restore, currentFile change).
// Without dedup, Railway gets N parallel requests for the same file —
// they race, the cache write collides, and the wall-clock time explodes.
// We keep a Map<driveId:itemId, Promise> so concurrent callers share
// one fetch, and cap active downloads at MAX_CONCURRENT to avoid
// saturating Railway's bandwidth.

const _inFlightFetches = new Map();
const _downloadQueue = [];
let _activeDownloads = 0;
// 6 concurrent direct-to-CDN fetches. The old 2-limit was me being overly
// cautious — the browser can happily handle many parallel fetches, and
// since each one goes direct to SharePoint's CDN (not through Railway),
// there's no backend bottleneck to protect against.
const MAX_CONCURRENT_SP_DOWNLOADS = 6;

function _acquireDownloadSlot() {
  if (_activeDownloads < MAX_CONCURRENT_SP_DOWNLOADS) {
    _activeDownloads++;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    _downloadQueue.push(() => {
      _activeDownloads++;
      resolve();
    });
  });
}

function _releaseDownloadSlot() {
  _activeDownloads--;
  const next = _downloadQueue.shift();
  if (next) next();
}

/**
 * Resolve a SharePoint file to a range-capable HTTP URL without fetching
 * the bytes. The returned URL can be handed directly to pdfjsLib.getDocument
 * so PDF.js uses HTTP Range requests — first page visible in ~2 seconds
 * regardless of total file size, matching SharePoint's own web UI.
 *
 * Returns: { url, filename, cacheDir, cacheHit, size }
 */
export async function resolveFileUrl(driveId, itemId, filename, opts = {}) {
  const { downloadUrl, filename: f, cacheDir: c, cacheHit: h, size } =
    await _resolveDirectUrl(driveId, itemId, filename, opts);
  return { url: downloadUrl, filename: f, cacheDir: c, cacheHit: h, size };
}

async function _resolveDirectUrl(driveId, itemId, filename, { silent = false } = {}) {
  // See fetchFileContent above — Bearer is optional, backend falls
  // back to app-only when the project has a binding for this drive.
  const token = await maybeAcquireGraphToken();
  const pidlyToken = (() => {
    try { return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || null; } catch { return null; }
  })();
  const projectId = currentProjectId();
  const res = await fetch(`${BACKEND_URL}/api/sharepoint/direct-url`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(pidlyToken ? { 'X-Pidly-Token': pidlyToken } : {}),
      ...(projectId ? { 'X-Pidly-Project-Id': projectId } : {}),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ driveId, itemId, filename }),
  });
  if (!res.ok) {
    let errMsg = `direct-url failed: ${res.status}`;
    try {
      const body = await res.json();
      errMsg = body.error?.message || body.error || errMsg;
    } catch {}
    throw new Error(errMsg);
  }
  return res.json();
}

/**
 * Fetch a SharePoint file as a reusable Blob, deduplicated across concurrent
 * callers for the same (driveId, itemId) and rate-limited so at most 2 SP
 * downloads are in flight at once.
 *
 * Fast path: resolve the pre-signed downloadUrl from our backend, then
 * have the browser fetch bytes DIRECTLY from SharePoint's CDN. Bypasses
 * Railway's egress entirely — normally where 30+ second tail-latency
 * lives for clients that are far from Railway's region.
 *
 * Fallback: if the direct SP fetch fails (CORS, network, expired URL),
 * stream through the backend's /content endpoint.
 *
 * Returns: { blob, filename, cacheDir, cacheHit }
 */
export function fetchFileBlob(driveId, itemId, filename, opts = {}) {
  const key = `${driveId}:${itemId}`;
  const existing = _inFlightFetches.get(key);
  if (existing) return existing;

  const promise = (async () => {
    const tStart = performance.now();
    const waitStart = performance.now();
    await _acquireDownloadSlot();
    const waitedMs = (performance.now() - waitStart).toFixed(0);
    try {
      // Resolve the backend URLs first (tiny JSON call).
      const tResolve = performance.now();
      const { downloadUrl, cachedUrl, filename: f, cacheDir: c, cacheHit: h } =
        await _resolveDirectUrl(driveId, itemId, filename, opts);
      const resolveMs = (performance.now() - tResolve).toFixed(0);

      // Fast path #1 — cached URL via CDN (Cloudflare edge when DNS is set up).
      // Repeat opens of the same file version hit the edge, sub-second.
      if (cachedUrl) {
        try {
          const tCached = performance.now();
          const cachedRes = await fetch(cachedUrl);
          if (cachedRes.ok) {
            const blob = await cachedRes.blob();
            // Defensive: if the backend served a 0-byte response (a
            // truncated cache entry that somehow slipped past server-side
            // checks), treat as a miss and fall through to direct SP CDN.
            if (blob.size === 0) {
              console.warn(`[SP] cached URL returned 0 bytes for ${filename} — falling through to direct CDN`);
            } else {
              const cachedMs = (performance.now() - tCached).toFixed(0);
              const cfStatus = cachedRes.headers.get('cf-cache-status') || 'n/a';
              console.log(`[SP timing] ${filename} — slotWait=${waitedMs}ms, resolveUrl=${resolveMs}ms, cachedUrl=${cachedMs}ms (cf=${cfStatus}), total=${(performance.now() - tStart).toFixed(0)}ms, size=${(blob.size / 1024 / 1024).toFixed(2)}MB`);
              return { blob, filename: f, cacheDir: c, cacheHit: h };
            }
          }
          // 404 = backend hasn't populated yet (first-time open), fall through.
        } catch (cachedErr) {
          console.warn(`[SP] cached URL failed: ${cachedErr.message}, trying direct SP CDN`);
        }
      }

      // Fast path #2 — direct from Microsoft's CDN.
      try {
        const tDirect = performance.now();
        const directRes = await fetch(downloadUrl, { mode: 'cors', credentials: 'omit', cache: 'no-store' });
        if (!directRes.ok) throw new Error(`SP CDN returned ${directRes.status}`);
        const blob = await directRes.blob();
        const directMs = (performance.now() - tDirect).toFixed(0);
        console.log(`[SP timing] ${filename} — slotWait=${waitedMs}ms, resolveUrl=${resolveMs}ms, directCdn=${directMs}ms, total=${(performance.now() - tStart).toFixed(0)}ms, size=${(blob.size / 1024 / 1024).toFixed(2)}MB`);
        return { blob, filename: f, cacheDir: c, cacheHit: h };
      } catch (directErr) {
        console.warn(`[SP timing] ${filename} — direct failed at ${(performance.now() - tStart).toFixed(0)}ms: ${directErr.message}, falling back to backend stream`);
      }

      // Last-resort fallback — stream through Railway.
      const tFallback = performance.now();
      const { response, filename: ff, cacheDir: cc, cacheHit: hh } =
        await fetchFileContent(driveId, itemId, filename, opts);
      const blob = await response.blob();
      console.log(`[SP timing] ${filename} — fallback streaming took ${(performance.now() - tFallback).toFixed(0)}ms, total=${(performance.now() - tStart).toFixed(0)}ms, size=${(blob.size / 1024 / 1024).toFixed(2)}MB`);
      return { blob, filename: ff, cacheDir: cc, cacheHit: hh };
    } finally {
      _releaseDownloadSlot();
    }
  })();

  _inFlightFetches.set(key, promise);
  promise.finally(() => _inFlightFetches.delete(key));
  return promise;
}

/**
 * Resolve any SharePoint or OneDrive URL (sharing link, folder URL, file URL)
 * to a drive item. Returns { driveId, itemId, name, isFolder, webUrl, siteId }.
 * Works for both SharePoint sites and personal OneDrive.
 */
export const resolveShareUrl = (url) =>
  spFetch('/api/sharepoint/resolve-share', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });

/** Check out (lock) a file on SharePoint. filename is passed through for
 *  the activity feed ("checked out <drawing>"). */
export const checkoutFile = (driveId, itemId, filename = null) =>
  libraryOrgOf(driveId) ? libraryCall(libraryOrgOf(driveId), `/files/${encodeURIComponent(itemId)}/checkout`, {}) :
  spFetch('/api/sharepoint/checkout', {
    method: 'POST',
    body: JSON.stringify({ driveId, itemId, filename }),
  });

/** Delete a file AT THE SOURCE (goes to the site's recycle bin). */
export const deleteSpItem = (driveId, itemId, filename = null) =>
  libraryOrgOf(driveId) ? deleteLibraryFile(libraryOrgOf(driveId), itemId) :
  spFetch('/api/sharepoint/delete-item', {
    method: 'POST',
    body: JSON.stringify({ driveId, itemId, filename }),
  });

/** Upload local changes + check in (unlock) on SharePoint. */
export const checkinFile = (driveId, itemId, localPath, comment = '', filename = null) =>
  libraryOrgOf(driveId) ? libraryCall(libraryOrgOf(driveId), `/files/${encodeURIComponent(itemId)}/checkin-working`, { comment }) :
  spFetch('/api/sharepoint/checkin', {
    method: 'POST',
    body: JSON.stringify({ driveId, itemId, localPath, comment, filename }),
  });

/** Discard checkout (unlock without uploading changes). */
export const discardCheckout = (driveId, itemId) =>
  libraryOrgOf(driveId) ? discardLibraryCheckout(libraryOrgOf(driveId), itemId) :
  spFetch('/api/sharepoint/discard-checkout', {
    method: 'POST',
    body: JSON.stringify({ driveId, itemId }),
  });

/**
 * Bulk-release checkouts / reset stale locks.
 *   all=false → release every lock the current user holds.
 *   all=true  → admin only: clear ALL pidly lock rows (reset stale locks).
 */
export const discardAllCheckouts = (all = false) =>
  spFetch('/api/sharepoint/discard-all-checkouts', {
    method: 'POST',
    body: JSON.stringify({ all: !!all }),
  });

/** Get file metadata + checkout status. */
export const getFileInfo = (driveId, itemId) =>
  spFetch(`/api/sharepoint/file-info/${encodeURIComponent(driveId)}/${encodeURIComponent(itemId)}`);

/**
 * Bulk checkout state from pidly's authoritative lock table — one cheap DB
 * read, no Graph. Returns { myUserId, locks: [{ driveId, itemId, userId,
 * userName, lockedAt }] }. Pass the project's driveIds to scope the result.
 */
export async function fetchSpLocks(driveIds = []) {
  const orgs = [...new Set(driveIds.map(libraryOrgOf).filter(Boolean))];
  const sp = driveIds.filter(d => !libraryOrgOf(d));
  // A workspace with only library documents never asks SharePoint at all.
  const parts = await Promise.all([
    sp.length ? spFetch(`/api/sharepoint/locks?driveIds=${encodeURIComponent(sp.join(','))}`) : null,
    ...orgs.map(o => libraryCall(o, '/locks')),
  ]);
  const out = { locks: [], myUserId: null };
  for (const p of parts) {
    if (!p) continue;
    out.locks.push(...(p.locks || []));
    out.myUserId = out.myUserId || p.myUserId || null;
  }
  return out;
}

/** A library file to the library's recycle bin (library.delete). */
async function deleteLibraryFile(orgId, itemId) {
  const r = await apiFetch(`/api/orgs/${encodeURIComponent(orgId)}/library/files/${encodeURIComponent(itemId)}`, { method: 'DELETE', quiet: true });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `Library error: ${r.status}`);
  return json;
}

/** Give back your own check-out, or release someone else's (library.manage). */
async function discardLibraryCheckout(orgId, itemId) {
  try {
    return await libraryCall(orgId, `/files/${encodeURIComponent(itemId)}/discard`, {});
  } catch (e) {
    if (e.status !== 409) throw e;
    return libraryCall(orgId, `/files/${encodeURIComponent(itemId)}/release`, {});
  }
}

/** Get drive info (versioning diagnostic). */
export const getDriveInfo = (driveId) =>
  spFetch(`/api/sharepoint/drive-info/${encodeURIComponent(driveId)}`);

/** Fetch full recursive folder tree from SharePoint via backend. */
/**
 * Create a NEW file in a linked folder — a SharePoint folder (Graph upload,
 * chunked session past 4MB) or a pidly Documents folder (`library:` drive).
 * Conflicts auto-rename server-side on both roads, so the returned item's
 * name is authoritative.
 * @param bytes Uint8Array | ArrayBuffer
 * @returns { item: {id, name, webUrl?}, backendFilename }
 */
export async function createSharePointFile(driveId, parentItemId, filename, bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const libOrg = libraryOrgOf(driveId);
  if (libOrg) {
    // pidly Documents: the library's own multipart upload, folderId = the
    // library folder id the linked-folder node carries as its itemId.
    const fd = new FormData();
    fd.append('files', new Blob([u8], { type: 'application/pdf' }), filename);
    if (parentItemId) fd.append('folderId', parentItemId);
    fd.append('onConflict', 'rename');
    const r = await apiFetch(`/api/orgs/${encodeURIComponent(libOrg)}/library/files`, {
      method: 'POST', body: fd, quiet: true,
    });
    let json = {};
    try { json = await r.json(); } catch { /* no body */ }
    if (!r.ok) throw new Error(json.error || `Library error: ${r.status}`);
    const made = (json.added && json.added[0]) || (json.versioned && json.versioned[0]) || null;
    if (!made) throw new Error(json.failed?.[0]?.error || 'The library did not store the file');
    // No backendFilename: the viewer's copy is minted per person by /cache
    // when the document is opened.
    return { success: true, item: { id: made.id, name: made.name }, backendFilename: null };
  }
  // btoa chokes on large call stacks — encode in chunks.
  let bin = '';
  for (let i = 0; i < u8.length; i += 0x8000) {
    bin += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000));
  }
  return spFetch('/api/sharepoint/create-file', {
    method: 'POST',
    body: JSON.stringify({
      driveId,
      parentItemId: parentItemId || null,
      filename,
      contentBase64: btoa(bin),
    }),
  });
}

export const fetchFolderTree = (driveId, folderId, siteId) =>
  libraryOrgOf(driveId) ? libraryCall(libraryOrgOf(driveId), `/tree${folderId ? `?folderId=${encodeURIComponent(folderId)}` : ''}`) :
  spFetch('/api/sharepoint/folder-tree', {
    method: 'POST',
    body: JSON.stringify({ driveId, folderId, siteId }),
  });
